"use client";
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "@/i18n/navigation";
import { ArrowLeft, ArrowRight, List, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";

interface PostNavItem {
  slug: string;
  title: string;
  date: string;
}

interface BlogPostNavProps {
  prev: PostNavItem | null;
  next: PostNavItem | null;
  allPosts: PostNavItem[];
  currentSlug: string;
  translations: {
    backToBlog: string;
    allPosts: string;
    previousPost: string;
    nextPost: string;
  };
}

export default function BlogPostNav({ prev, next, allPosts, currentSlug, translations }: BlogPostNavProps) {
  const [listOpen, setListOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popoutRef = useRef<HTMLDivElement>(null);
  const [btnRect, setBtnRect] = useState({ top: 0, left: 0, width: 0 });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        popoutRef.current && !popoutRef.current.contains(e.target as Node) &&
        btnRef.current && !btnRef.current.contains(e.target as Node)
      ) {
        setListOpen(false);
      }
    };
    if (listOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [listOpen]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setListOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    const lenis = typeof window !== "undefined" ? (window as unknown as Record<string, unknown>).__lenis as { stop: () => void; start: () => void } | undefined : undefined;
    if (listOpen) {
      lenis?.stop();
      document.body.style.overflow = "hidden";
    } else {
      lenis?.start();
      document.body.style.overflow = "";
    }
    return () => {
      lenis?.start();
      document.body.style.overflow = "";
    };
  }, [listOpen]);

  const openList = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setBtnRect({ top: rect.top, left: rect.left, width: rect.width });
    }
    setIsMobile(window.innerWidth < 640);
    setListOpen(true);
  };

  return (
    <>
      <div className="py-[24px] lg:py-[32px] mb-[60px] lg:mb-[100px] relative">
        <div className="absolute inset-0 bg-gradient-to-b from-[rgba(10,0,31,0.35)] to-[rgba(10,0,31,0.4)] z-0" />
        <div className="container relative z-1">
          <div className="flex items-center justify-between gap-[12px]">
            {/* Previous */}
            {prev ? (
              <Link
                href={`/blog/${prev.slug}`}
                className="group flex items-center gap-[12px] text-foreground/40 hover:text-foreground transition-colors duration-300 min-w-0 flex-1"
              >
                <ArrowLeft className="w-5 h-5 shrink-0 group-hover:-translate-x-1 transition-transform duration-300" />
                <div className="min-w-0 hidden sm:block">
                  <p className="text-[16px] lg:text-[20px] font-semibold text-foreground/90 group-hover:text-foreground transition-colors duration-300">
                    {translations.previousPost}
                  </p>
                  <p className="text-[13px] font-light text-foreground/40 mt-[4px]">
                    {prev.title}
                  </p>
                </div>
              </Link>
            ) : (
              <div className="flex-1" />
            )}

            {/* All posts circle button */}
            <button
              ref={btnRef}
              onClick={openList}
              className="w-[48px] h-[48px] shrink-0 rounded-full border border-white/[0.08] bg-[#121124]/60 flex items-center justify-center text-foreground/40 hover:text-white hover:border-white/[0.2] hover:bg-[#1a1538] transition-all duration-300 cursor-pointer"
              aria-label={translations.allPosts}
              title={translations.allPosts}
            >
              <List className="w-[18px] h-[18px]" />
            </button>

            {/* Next */}
            {next ? (
              <Link
                href={`/blog/${next.slug}`}
                className="group flex items-center gap-[12px] text-foreground/40 hover:text-foreground transition-colors duration-300 min-w-0 flex-1 justify-end text-right"
              >
                <div className="min-w-0 hidden sm:block">
                  <p className="text-[16px] lg:text-[20px] font-semibold text-foreground/90 group-hover:text-foreground transition-colors duration-300">
                    {translations.nextPost}
                  </p>
                  <p className="text-[13px] font-light text-foreground/40 mt-[4px]">
                    {next.title}
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 shrink-0 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            ) : (
              <div className="flex-1" />
            )}
          </div>
        </div>
      </div>

      {/* Portalled to body — escapes all stacking contexts */}
      {typeof document !== "undefined" && createPortal(
        <AnimatePresence>
          {listOpen && (
            <>
              {/* Dark overlay */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 bg-[#0A001F]/80 z-[9998]"
                onClick={() => setListOpen(false)}
              />

              {/* Close button — same position as circle button on desktop, centered bottom on mobile */}
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.15 }}
                onClick={() => setListOpen(false)}
                className="fixed w-[48px] h-[48px] rounded-full border border-white/[0.15] bg-[#1a1538] flex items-center justify-center text-white z-[9999] cursor-pointer hover:border-white/[0.3] transition-colors duration-300"
                style={isMobile
                  ? { bottom: 24, left: "50%", transform: "translateX(-50%)" }
                  : { top: btnRect.top, left: btnRect.left, width: btnRect.width, height: btnRect.width }
                }
                aria-label="Close"
              >
                <X className="w-[18px] h-[18px]" />
              </motion.button>

              {/* Popout list */}
              <motion.div
                ref={popoutRef}
                initial={{ opacity: 0, y: 8, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.95 }}
                transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                className="fixed max-h-[50vh] overflow-y-auto rounded-[16px] bg-[#121124] border border-white/[0.08] shadow-[0_8px_32px_rgba(0,0,0,0.5)] z-[9999] scrollbar-hide"
                style={isMobile
                  ? { left: 16, right: 16, bottom: 84 }
                  : {
                      bottom: `calc(100vh - ${btnRect.top}px + 12px)`,
                      left: btnRect.left + btnRect.width / 2,
                      transform: "translateX(-50%)",
                      width: 400,
                    }
                }
                data-lenis-prevent
              >
                <div className="p-[16px]">
                  <p className="text-[12px] font-medium text-foreground/35 uppercase tracking-wider mb-[12px] px-[8px]">
                    {translations.allPosts}
                  </p>
                  <ul className="space-y-[2px]">
                    {allPosts.map((post) => {
                      const isCurrent = post.slug === currentSlug;
                      return (
                        <li key={post.slug}>
                          {isCurrent ? (
                            <div className="px-[12px] py-[10px] rounded-[10px] bg-white/[0.04]">
                              <p className="text-[14px] font-medium text-white/30 leading-[1.4] truncate">{post.title}</p>
                              <p className="text-[11px] font-light text-foreground/25 mt-[2px]">{post.date}</p>
                            </div>
                          ) : (
                            <Link
                              href={`/blog/${post.slug}`}
                              onClick={() => setListOpen(false)}
                              className="block px-[12px] py-[10px] rounded-[10px] hover:bg-white/[0.04] transition-colors duration-200 group/item"
                            >
                              <p className="text-[14px] font-medium text-foreground/80 leading-[1.4] truncate group-hover/item:text-white transition-colors duration-200">{post.title}</p>
                              <p className="text-[11px] font-light text-foreground/35 mt-[2px]">{post.date}</p>
                            </Link>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
